import styled from "@emotion/styled";
import type { FC } from "react";

import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import type { LocalAudioRecordingBlobRecord } from "@/domains/dexie-db/models/audio-recording";

export type AudioRecordingBlobStats = {
  totalDurationLabel: string;
  totalDurationMs: number;
  totalSizeLabel: string;
  totalSizeBytes: number;
  uploadedCount: number;
};

export const computeAudioRecordingBlobStats = (
  blobs: LocalAudioRecordingBlobRecord[]
): AudioRecordingBlobStats => {
  if (!blobs.length) {
    return {
      totalDurationLabel: "00:00",
      totalDurationMs: 0,
      totalSizeLabel: "0 KB",
      totalSizeBytes: 0,
      uploadedCount: 0,
    };
  }

  const totalDurationMs = blobs.reduce((total, blob) => total + blob.durationMs, 0);
  const totalSizeBytes = blobs.reduce((total, blob) => total + blob.byteLength, 0);
  const uploadedCount = blobs.filter(blob => blob.isUploaded).length;

  const minutes = Math.floor(totalDurationMs / 60000);
  const seconds = Math.floor((totalDurationMs % 60000) / 1000);
  const totalDurationLabel = `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;

  const totalSizeKb = totalSizeBytes / 1024;
  const totalSizeLabel =
    totalSizeKb < 1024 ? `${totalSizeKb.toFixed(1)} KB` : `${(totalSizeKb / 1024).toFixed(2)} MB`;

  return {
    totalDurationLabel,
    totalDurationMs,
    totalSizeLabel,
    totalSizeBytes,
    uploadedCount,
  };
};

type Props = {
  blobs: LocalAudioRecordingBlobRecord[];
  emptyMessage?: string;
};

export const AudioRecordingBlobList: FC<Props> = ({ blobs, emptyMessage }) => {
  if (!blobs.length) {
    return (
      <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
        {emptyMessage ?? "No blobs recorded yet."}
      </MDSText>
    );
  }

  return (
    <BlobsList>
      {blobs.map(blob => (
        <BlobsListItem key={blob.id}>
          <MDSText variant={MDSTextVariant.TextXs}>
            #{blob.blobIndex.toString().padStart(3, "0")} · {(blob.byteLength / 1024).toFixed(1)} KB
            · {(blob.durationMs / 1000).toFixed(2)} s · {blob.isUploaded ? "Uploaded" : "Local"}
          </MDSText>
        </BlobsListItem>
      ))}
    </BlobsList>
  );
};

const BlobsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BlobsListItem = styled.div`
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.background.card.default};
`;
